import { createStore } from '@oneappexchange/lwc-wired-store';
import Navigo from 'navigo';
// Scheme: https://tools.ietf.org/html/rfc3986#section-3.1
// Absolute URL: https://tools.ietf.org/html/rfc3986#section-4.3
const ABSOLUTE_URL_REGEX = /^[a-zA-Z][a-zA-Z\d+\-.]*?:/;
const initialState = {
  url: '',
  queryString: '',
  hashString: '',
  data: null,
  params: null,
  isLoading: false,
  pageComponent: undefined,
  beforeUnload: undefined
};
export function createRouterStore({
  routes,
  rootUrl = '/',
  storeName = 'ROUTER',
  developmentMode = false,
  routeLoadDurationCallback
}) {
  const navigo = new Navigo(rootUrl);
  const routerStore = createStore(initialState, set => ({
    setMatch(match) {
      set(function setMatch(draft) {
        Object.keys(match).forEach(key => {
          draft[key] = match[key];
        });
      });
    },
    beginLoadingRoute() {
      set(function beginLoadingRoute(draft) {
        draft.isLoading = true;
      });
    },
    endLoadingRoute(pageComponent) {
      set(function endLoadingRoute(draft) {
        draft.isLoading = false;
        draft.pageComponent = pageComponent;
      });
    },
    setBeforeUnload(beforeUnload) {
      set(function setBeforeUnload(draft) {
        draft.beforeUnload = beforeUnload;
      });
    }
  }), storeName, developmentMode);
  // Initialize the Navigo router
  const timerMap = new Map();
  navigo.hooks({
    before(done, match) {
      const url = match.url === '' ? 'home' : match.url;
      timerMap.set(url, performance.now());
      routerStore.setMatch(match);
      done();
    },
    leave(done, match) {
      function testUnload(match) {
        const {
          beforeUnload
        } = routerStore.getState();
        if (beforeUnload) {
          let message;
          let onClose;
          const data = beforeUnload(match);
          if (typeof data === 'string') {
            message = data;
          } else if (data) {
            message = data.message;
            onClose = data.onClose;
          }
          if (message && !confirm(message)) {
            timerMap.delete(match.url);
            onClose(false);
            return true;
          }
          if (onClose) {
            onClose(true);
          }
        }
        return false;
      }
      if (Array.isArray(match) && match.some(testUnload) || testUnload(match)) {
        done(false);
        return false;
      }
      done();
    },
    after(match) {
      const url = match.url === '' ? 'home' : match.url;
      const startTime = timerMap.get(url) || performance.now();
      routeLoadDurationCallback?.(url, performance.now() - startTime);
      timerMap.delete(url);
    }
  });
  const routeKeys = Object.keys(routes);
  routeKeys.filter(test => test !== '*').forEach(route => {
    navigo.on(route.startsWith('/') ? route : `/${route}`, async () => {
      routerStore.beginLoadingRoute();
      try {
        routerStore.endLoadingRoute((await routes[route]()).default);
      } catch (error) {
        console.error(`Failed to load component for route '${route}'`, error);
        routerStore.endLoadingRoute();
      }
    });
  });
  if (routes['*']) {
    navigo.notFound(async () => {
      routerStore.beginLoadingRoute();
      try {
        routerStore.endLoadingRoute((await routes['*']()).default);
      } catch (error) {
        console.error(`Failed to load component for 404 route ('*')`, error);
        routerStore.endLoadingRoute();
      }
    });
  }
  navigo.resolve();
  function testIsNavigoLink(link) {
    // Absolute paths are not handled correctly by Navigo, returning false here lets the browser handle them.
    if (ABSOLUTE_URL_REGEX.test(link)) {
      return false;
    }
    let match = link;
    // Extract params out of the route
    const paramIndex = link.indexOf('?');
    if (paramIndex !== -1) {
      match = link.substring(0, paramIndex);
    }
    // Match on the exact route, or on /route
    return routeKeys.find(test => test === match || `/${test}` === match) != null;
  }
  function navigate(to, options) {
    navigo.navigate(to, options);
  }
  return {
    routerStore,
    navigate,
    testIsNavigoLink
  };
}
export function selectUrl(state) {
  return state.url;
}
export function selectQueryString(state) {
  return state.queryString;
}
export function selectHashString(state) {
  return state.hashString;
}
export function selectData(state) {
  return state.data;
}
export function selectParams(state) {
  return state.params;
}
export function selectPageComponent(state) {
  return state.pageComponent;
}